import type { VehicleSide } from "@/lib/api/types"
import { CORNER_SIDE, ZONE_SIDE } from "../shot-plan"
import type { Corner, PlannedShot, ShotId } from "../types"
import { sideAxes, sideMatchesPlan } from "../verdict"

// Place photos that arrived without a walk (gallery import, desktop drop,
// server images with no shot id) into the open slots of the plan.
//
// Order, most certain first:
//   1. A corner photo fills that corner's whole-car slot.
//   2. A face photo (left side, front, …) fills the close-up for a damaged
//      panel on that face.
//   3. …then a closer shot it's compatible with.
//   4. …then a whole-car slot that corner can see (front → a front corner).
//   5. Anything left fills "a close-up of the damage" when the plan has one.
//   6. The rest are extras — still sent, never dropped.
//
// `framing` comes from the on-device detector when it ran: "wide" = the whole
// car is in view, "close" = no car / a filled frame. It vetoes the wrong kind
// of slot. Pure; unit-tested.

export interface SortablePhoto {
  key: string
  side: VehicleSide | null
  framing?: "wide" | "close" | null
}

export interface SortResult {
  placed: Map<ShotId, string>
  extras: string[]
}

const CORNER_OF_SIDE = new Map<VehicleSide, Corner>(
  (Object.entries(CORNER_SIDE) as Array<[Corner, VehicleSide]>).map(([c, s]) => [s, c]),
)

export function sortIntoSlots(
  photos: readonly SortablePhoto[],
  plan: readonly PlannedShot[],
  taken: ReadonlySet<ShotId>,
): SortResult {
  const placed = new Map<ShotId, string>()
  const free = new Set(photos.map((p) => p.key))
  const open = (shot: PlannedShot) => !taken.has(shot.id) && !placed.has(shot.id)
  const place = (shot: PlannedShot, photo: SortablePhoto) => {
    placed.set(shot.id, photo.key)
    free.delete(photo.key)
  }
  const pending = () => photos.filter((p) => free.has(p.key))

  // 1. Exact corners → whole-car slots.
  for (const photo of pending()) {
    if (photo.framing === "close" || !photo.side) continue
    const corner = CORNER_OF_SIDE.get(photo.side)
    const slot = corner && plan.find((s) => s.id === `wide:${corner}`)
    if (slot && open(slot)) place(slot, photo)
  }

  // 2. Faces → close-ups of a damaged panel on that face.
  for (const photo of pending()) {
    if (photo.framing === "wide" || !photo.side) continue
    const slot = plan.find(
      (s) => s.kind === "closeup" && s.zone && open(s) && ZONE_SIDE[s.zone] === photo.side,
    )
    if (slot) place(slot, photo)
  }

  // 3. Closer shots that accept the photo's side.
  for (const photo of pending()) {
    if (photo.framing === "wide" || !photo.side) continue
    const slot = plan.find(
      (s) => s.kind === "closer" && open(s) && sharesAnAxis(s.side, photo.side),
    )
    if (slot) place(slot, photo)
  }

  // 4. A whole-car slot that can see the photo's face (and actually shares
  //    one with it — a roof shot is not a corner shot).
  for (const photo of pending()) {
    if (photo.framing === "close" || !photo.side) continue
    const slot = plan.find(
      (s) =>
        s.kind === "wide" &&
        open(s) &&
        sideMatchesPlan(s, photo.side) &&
        sharesAnAxis(s.side, photo.side),
    )
    if (slot) place(slot, photo)
  }

  // 5. "A close-up of the damage" (Not sure walks) takes the next leftover.
  const anyCloseup = plan.find((s) => s.id === "closeup:any")
  if (anyCloseup && open(anyCloseup)) {
    const photo = pending().find((p) => p.framing !== "wide")
    if (photo) place(anyCloseup, photo)
  }

  return { placed, extras: pending().map((p) => p.key) }
}

function sharesAnAxis(a: VehicleSide | null, b: VehicleSide | null): boolean {
  const x = sideAxes(a)
  const y = sideAxes(b)
  return (!!x.end && x.end === y.end) || (!!x.lr && x.lr === y.lr)
}
